<template>
  <section v-if="project">
    <article class="card">
      <div class="card__padding d-flex justify-content-between align-items-center">
        <div>
          <h3 class="title__h3 bold">
            {{ t('literal.members') }}
          </h3>

          <template v-if="project.limit_of_members">
            <p class="label--light">
              <template v-if="hasReachedLimitOfMembers">
                {{ t('literal.project_has_reached_maximum_members_allowed') }}
              </template>

              <template v-else>
                {{ t('plural.project_limited_to_x_additional_member', { x: limitOfMembersLeft }, limitOfMembersLeft) }}
              </template>
            </p>
          </template>
        </div>

        <button
          class="btn btn__color--prim btn__invite d-flex align-items-center"
          :disabled="project.limit_of_members && hasReachedLimitOfMembers || loader"
          @click.prevent="openInviteModal()"
          v-if="project.is_members_invite_allowed"
        >
          <fa-icon :icon="['fal', 'plus']" />

          <span class="d-none d-md-block">{{ t('literal.invite_members') }}</span>
        </button>
      </div>

      <div class="card__padding no-padding--t">
        <form-notification
          class="margin__b--24"
          :keys="error"
          v-if="!show_invite_modal"
        />

        <ul class="members__ul">
          <li
            class="d-flex justify-content-center padding__tb--12"
            v-if="loader"
          >
            <app-spinner />
          </li>

          <template v-else>
            <li
              class="members__li"
              v-for="user in users"
              :key="user.id"
            >
              <div
                v-if="user.is_invite"
                class="members__pending d-flex justify-content-center align-items-center"
              >
                <fa-icon :icon="['fal', 'envelope']" />
              </div>

              <app-avatar
                v-else
                :avatar="user.avatar"
                size="4"
              />

              <div class="member__data">
                <div class="d-flex align-items-center">
                  <p>
                    <span class="bold">{{ user.firstname }}</span> {{ user.lastname }}
                  </p>

                  <span
                    v-if="user.is_invite"
                    class="status status--pending"
                  >
                    {{ t('literal.invite_pending') }}
                  </span>

                  <span
                    class="status status--admin margin__l--6"
                    v-else-if="user.is_admin"
                  >
                    {{ t('literal.admin') }}
                  </span>
                </div>

                <p class="color--mg">
                  {{ user.email }}
                </p>
              </div>

              <app-floating-menu v-if="!user.is_admin">
                <template #options="{ hide }">
                  <ul>
                    <li
                      class="menu__li"
                      v-if="user.is_invite"
                    >
                      <button
                        class="menu__button color--danger"
                        @click.prevent="() => {
                          hide()
                          handleCancelInvite(user.id)
                        }"
                      >
                        <fa-icon
                          class="margin__r--6"
                          :icon="['fal', 'ban']"
                          fixed-width
                        />

                        {{ t('literal.cancel_invite') }}
                      </button>
                    </li>

                    <template v-else>
                      <li
                        class="menu__li"
                        v-if="members?.isProjectAdmin(authUser)"
                      >
                        <button
                          class="menu__button"
                          @click.prevent="() => {
                            hide()
                            handleTransferOwner(user.id)
                          }"
                        >
                          <fa-icon
                            class="margin__r--6"
                            :icon="['fal', 'user-gear']"
                            fixed-width
                          />

                          {{ t('literal.transfer_admin') }}
                        </button>
                      </li>

                      <li class="menu__li">
                        <button
                          class="menu__button color--danger"
                          @click.prevent="() => {
                            hide()
                            handleDeleteMember(user.id)
                          }"
                        >
                          <fa-icon
                            class="margin__r--6"
                            :icon="['fal', 'circle-minus']"
                            fixed-width
                          />

                          {{ t('literal.remove_from_project') }}
                        </button>
                      </li>
                    </template>
                  </ul>
                </template>
              </app-floating-menu>
            </li>
          </template>
        </ul>
      </div>
    </article>

    <article
      class="card card__padding margin__t--24"
      v-if="showInvitationLink"
    >
      <h3 class="title__h3 bold">
        {{ t('literal.invitation_link') }}
      </h3>

      <p class="margin__t--6">
        {{ t('private_settings_members.invitation_link_description') }}
      </p>

      <button
        class="invite-link margin__t--18 padding--12 bg__color--vdw"
        @click.prevent="handleClickCopyLink($event)"
      >
        <span class="invite-link__url">
          {{ project.invite_url }}
        </span>

        <fa-icon
          class="invite-link__icon"
          :icon="['fal', 'copy']"
        />
      </button>
    </article>

    <procedure-invite-members
      v-if="show_invite_modal"
      :project-slug="project.slug"
      :limit="limitOfMembersLeft"
      @close="closeInviteModal()"
    />
  </section>
</template>

<script setup>
import { computed, defineAsyncComponent, onUnmounted } from 'vue'
import { storeToRefs } from 'pinia'
import { useRouter } from 'vue-router'
import { useProjectStore } from '@/stores/ProjectStore'
import { useNotificationStore } from '@/stores/NotificationStore'
import { useAuthenticationStore } from '@/stores/AuthenticationStore'
import { useModalStore } from '@/stores/ModalStore'
import { useModals } from '@/composables/app/useModals'
import { useI18n } from '@/vendors/i18n'
import { useHead } from '@unhead/vue'
import { copyToClipboard } from '@/utils/utils'
import { sortBy as _sortBy } from 'lodash'

const AppAvatar = defineAsyncComponent(() => import('&/atoms/AppAvatar'))
const AppSpinner = defineAsyncComponent(() => import('&/atoms/AppSpinner'))
const FormNotification = defineAsyncComponent(() => import('&/molecules/FormNotification'))
const AppFloatingMenu = defineAsyncComponent(() => import('&/organisms/AppFloatingMenu'))
const ProcedureInviteMembers = defineAsyncComponent(() => import('&/modules/project/ProcedureInviteMembers'))

const { projectSlug } = defineProps({ projectSlug: { type: String, required: true } })

const { t } = useI18n()
const { push } = useRouter()
const { getProject, getProjectMembers, getInvites, fetchProject, fetchInvites, fetchProjectMembers, deleteProjectMember, deleteProjectInvite, postProjectMembersOwner, $resetStore } = useProjectStore()
const { error } = storeToRefs(useProjectStore())
const { postSideNotification } = useNotificationStore()
const { authUser } = storeToRefs(useAuthenticationStore())
const { postConfirmModal } = useModalStore()
const { show_modal: show_invite_modal, openModal: openInviteModal, closeModal: closeInviteModal } = useModals()

const query = computed(() => ({ active: true, 'sort_by_field[field]': 'created_at', 'sort_by_field[direction]': 'asc' }))

const { project } = getProject({ id: projectSlug })
const { members, loader: membersLoader } = getProjectMembers({ project_id: projectSlug })
const { invites, loader: invitesLoader } = getInvites({ project_id: project.value?.id, query })

const users = computed(() => [
  ..._sortBy(members.value || [], member => member.role === 'admin' ? 0 : 1),
  ...(invites.value || []).map(invite => ({ ...invite, is_invite: true }))
])
const loader = computed(() => membersLoader.value || invitesLoader.value)

useHead({ title: computed(() => `${t('literal.members')} / ${t('literal.settings')} / ${project.value?.name}`) })

const limitOfMembersLeft = computed(() => {
  if (!project.value?.limit_of_members || !invites.value) return 0
  return Math.max(0, project.value.members_left - invites.value.length)
})
const hasReachedLimitOfMembers = computed(() => limitOfMembersLeft.value <= 0)
const showInvitationLink = computed(() => project.value?.is_members_invite_allowed && project.value?.invite_url && (!project.value?.limit_of_members || project.value?.limit_of_members && !hasReachedLimitOfMembers.value))

const handleTransferOwner = user_id => postConfirmModal({
  title: 'literal.confirm_admin_transfer',
  text: ['literal.you_re_about_to_transfer_your_admin_rights', 'literal.you_will_lose_access_to_admin_settings_and_wont_be_able_to_undo_this', 'literal.only_the_new_admin_can_manage_roles_after_the_transfer', 'literal.are_you_sure_you_want_to_continue'],
  callback: async () => {
    try {
      await postProjectMembersOwner({ project_id: projectSlug, user_id })
    } catch (e) {
      error.value = e.data?.errors?.length ? e.data.errors : 'failed_to_transfer_project_ownership'
    }
  }
})

const handleDeleteMember = user_id => postConfirmModal({
  title: 'confirm_modale.project_remove_member',
  callback: async () => {
    try {
      await deleteProjectMember({ project_id: projectSlug, user_id })
    } catch (e) {
      error.value = e.data?.errors?.length ? e.data.errors : 'failed_to_delete_member'
    }

    await fetchProject({ id: projectSlug })

    if (!project.value.isAuthorized(authUser.value)) return push({ name: 'Project' })

    try {
      await fetchProjectMembers({ project_id: projectSlug })
    } catch {
      postSideNotification({ text: 'literal.please_refresh_your_page', type: 'info', delay: 0 })
    }
  }
})

const handleCancelInvite = invite_id => postConfirmModal({
  title: 'confirm_modale.project_cancel_invite',
  callback: async () => {
    try {
      await deleteProjectInvite({ project_id: project.value.id, invite_id })
    } catch (e) {
      error.value = e.data?.errors?.length ? e.data.errors : 'failed_to_cancel_invite'
    }

    try {
      await fetchInvites({ project_id: project.value.id, query })
    } catch {
      postSideNotification({ text: 'literal.please_refresh_your_page', type: 'info', delay: 0 })
    }
  }
})

const handleClickCopyLink = ({ currentTarget }) => {
  copyToClipboard(project.value.invite_url, currentTarget)
    .then(() => postSideNotification({ text: 'literal.the_link_has_been_copied_to_the_clipboard' }))
    .catch(() => postSideNotification({ text: 'literal.error_the_text_could_be_copied', type: 'error', delay: 10000 }))
}

onUnmounted(() => $resetStore('error'))
</script>

<style lang="scss" scoped>
.members {
  &__li {
    font-size: rem(14px);
    display: grid;
    grid-template-columns: 48px auto 30px;
    gap: 12px;
    align-items: center;

    @include mq(md) {
      font-size: rem(16px);
    }

    &:not(:first-child) {
      padding-top: 18px;
      border-top: 1px solid rgba($dg, 0.1);
    }

    &:not(:last-child) {
      padding-bottom: 18px;
    }
  }

  &__data {
    display: flex;
    flex-direction: column;
  }

  &__pending {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background-color: rgba($dg, 0.4);
    font-size: rem(18px);
    color: $white;
  }
}

.status {
  font-size: 12px;
  padding: 3px 6px;
  border-radius: 2px;
  font-weight: bold;
  white-space: nowrap;

  @include mq(sm) {
    font-size: 14px;
  }

  &--admin {
    background-color: tint($color--primary, 90%, 95%);
    color: $color--primary;
  }

  &--pending {
    background-color: tint($color--warning, 90%, 95%);
    color: $color--warning;
  }
}

.btn {
  &__invite {
    font-size: rem(14px);
    padding: 6px 12px;

    @include mq(sm) {
      font-size: rem(16px);
      gap: 6px;
      padding: 6px 24px;
    }
  }
}

.invite-link {
  padding: 12px;
  border-radius: 3px;
  transition: all $transition__duration;
  display: grid;
  grid-template-columns: auto 14px;
  align-items: center;
  gap: 6px;
  width: 100%;

  &:hover {
    background: rgba($dw, .7);
    color: $color--secondary;

    .invite-link {
      &__icon {
        color: $color--secondary;
      }
    }
  }

  &__url {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    direction: rtl;
    text-align: left;
  }

  &__icon {
    color: rgba($dg, 0.6);
  }
}
</style>
